// @ts-ignore
import Keycloak, {KeycloakInitOptions} from "keycloak-js";
import React, { createContext, PropsWithChildren, ReactElement, useCallback, useEffect, useState } from 'react'

interface AuthContextValues {
    isAuthenticated: boolean
    client?: Keycloak
}

const defaultAuthContextValues: AuthContextValues = {
    isAuthenticated: false,
}

const AuthContext = createContext<AuthContextValues>(defaultAuthContextValues)

type AuthContextProviderProps = PropsWithChildren & {
    authClient: Keycloak & { didInitialize?: boolean }
    LoadingComponent?: ReactElement | null
    initOptions: KeycloakInitOptions
}

const AuthContextProvider = ({ children, authClient, initOptions, LoadingComponent }: AuthContextProviderProps) => {
    const [isAuthenticated, setAuthenticated] = useState(false)

    const onReady = useCallback(() => {
        authClient.authenticated && !!authClient.idToken && !!authClient.token && setAuthenticated(true)
    }, [authClient.authenticated, authClient.idToken, authClient.token])

    const onError = useCallback((err: unknown) => {
        throw new Error(err as string)
    }, [])

    const refreshToken = useCallback(() => {
        authClient.authenticated && authClient.updateToken(5)
    }, [authClient])

    useEffect(() => {
        if (!authClient) return

        authClient.onReady = onReady
        authClient.onTokenExpired = refreshToken

        if (!authClient?.didInitialize) {
            authClient.init(initOptions).catch(onError)
        }
    }, [authClient, initOptions, onReady, onError, refreshToken])

    if (!isAuthenticated) {
        return LoadingComponent ?? null
    }

    return (
        <AuthContext.Provider value={{ isAuthenticated, client: authClient }}>{children}</AuthContext.Provider>
    )
}

export { AuthContext, AuthContextProvider }
